import { Links } from "@modules/Navbar/components/Links";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "@tanstack/react-router";
import { useLocale } from "@modules/shared/hooks/useLocale";
import { useEffect } from "react";
import { X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@ui/components/ui/sheet";
import {
  getInfoPageTo,
  mobileMenuInfoPages,
} from "@modules/shop/config/info-pages";
import { ProfileButton } from "./ProfileButton";
import { LanguageSwitcher } from "./LanguageSwitcher";

type Props = {
  showNavigation: boolean;
  setShowNavigation: React.Dispatch<React.SetStateAction<boolean>>;
};

export const NavbarModal: React.FC<Props> = ({
  showNavigation,
  setShowNavigation,
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const locale = useLocale();

  useEffect(() => {
    return () => {
      setShowNavigation(false);
    };
  }, [pathname, setShowNavigation]);

  return (
    <Sheet
      open={showNavigation}
      onOpenChange={(open) => setShowNavigation(open)}
    >
      <SheetContent
        side="left"
        className="w-full border-0 bg-background-7 p-0 text-text-1"
        hideCloseButton
      >
        <SheetTitle className="sr-only">
          {t("mobileMenu", "Navigation")}
        </SheetTitle>
        <div className="flex h-full w-full flex-col">
          <div className="flex items-center justify-between px-5 py-4">
            <ProfileButton isMobileView />
            <div className="flex items-center gap-2">
              <LanguageSwitcher isMobileView />
              <button
                type="button"
                className="flex h-9 w-9 items-center justify-center rounded-full text-text-1 transition-colors duration-200 hover:bg-background-6"
                onClick={() => setShowNavigation(false)}
                aria-label={t("closeMenu", "Close menu")}
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="flex flex-[4] items-center">
            <div className="grid h-fit w-full grid-cols-15 gap-6 text-large leading-medium max-md:grid-rows-[min-content] [&>*]:col-span-padded">
              <Links
                isMobileView
                closeNavigation={() => setShowNavigation(false)}
              />
            </div>
          </div>
          <div className="flex flex-1 flex-col justify-between pb-8 font-primary">
            <div className="grid w-full grid-cols-15 text-small -tracking-[0.02em] max-md:grid-rows-[min-content]">
              <div className="col-start-2 col-end-8 flex flex-col gap-4">
                {mobileMenuInfoPages.slice(0, 3).map((page) => (
                  <Link
                    key={page.path}
                    className="hover:underline"
                    to={getInfoPageTo(page.path)}
                    params={{ locale }}
                  >
                    {t(`Footer.${page.footerKey}`)}
                  </Link>
                ))}
              </div>
              <div className="col-start-9 col-end-[-2] flex flex-col gap-4">
                {mobileMenuInfoPages.slice(3).map((page) => (
                  <Link
                    key={page.path}
                    className="hover:underline"
                    to={getInfoPageTo(page.path)}
                    params={{ locale }}
                  >
                    {t(`Footer.${page.footerKey}`)}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
